import type { UseMutationResult } from 'react-query';
import { useMutation, useQueryClient } from 'react-query';

import type {
  IApiCreateArticleRequestBody,
  IApiCreateArticleResponseBody,
} from '../ArticlesApi.types';
import { useApi } from '~/lib/api/useApi';
import { onboardingStatusQueryKeyFactory } from '../../Onboarding/hooks/useOnboardingStatusQuery';
import { articlesQueryKeyFactory } from './useArticlesQuery';

export type IUseCreateArticleMutationValues = {
  appId: string;
  body: IApiCreateArticleRequestBody;
};

export const useCreateArticleMutation = (): UseMutationResult<
  IApiCreateArticleResponseBody,
  unknown,
  IUseCreateArticleMutationValues
> => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: IUseCreateArticleMutationValues) =>
      api.articles.create(values.appId, values.body),
    onSuccess: async (_, values) => {
      await Promise.all(
        (values.body.categoriesId ?? [])
          .map((categoryId) => ({
            queryKey: articlesQueryKeyFactory({
              appId: values.appId,
              body: {
                category: categoryId,
              },
            }),
          }))
          .map((filters) => queryClient.invalidateQueries(filters)),
      );

      await queryClient.invalidateQueries({
        queryKey: onboardingStatusQueryKeyFactory({
          body: {
            target: 'application',
            id: values.appId,
          },
        }),
      });
    },
  });
};
